/**
 * One line at the top of the bell's dropdown (TRUS-759) saying when this
 * browser will NOT show desktop notifications, with "Turn on" where a click can
 * fix it. The app-wide banner hides after "Not now" and Settings is out of the
 * way, so without this a user with notifications off had no idea why nothing
 * popped up. Renders nothing when push is on, still loading, or impossible here
 * (desktop app, pop-outs, browsers without the Push API).
 */
import { BellOff } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useWebPushControl } from '@/hooks/useWebPush';
import { getLastWebPushFailure } from '@/lib/webPush';
import { toastWebPushFailure, webPushFailureText } from '@/components/notifications/webPushFailureText';
import { usePushMasterSwitch } from '@/hooks/usePushMasterSwitch';

export function BellPushStatus() {
  const { t } = useAppTranslation();
  const { status, failure, busy, enable } = useWebPushControl('staff');
  const { enabled: pushOnAllDevices } = usePushMasterSwitch(true);

  if (status === 'subscribed' || status === 'loading' || status === 'unsupported') return null;

  let detail: string;
  if (pushOnAllDevices === false) {
    detail = t('messaging.webPush.bellMasterOff', 'Desktop notifications are off in your notification settings.');
  } else if (status === 'denied') {
    detail = t('messaging.webPush.bellBlocked', 'Desktop notifications are blocked. Allow notifications for this site in your browser settings.');
  } else if (status === 'disabled') {
    detail = t('messaging.webPush.statusUnavailable', 'Not available yet: browser notifications are not set up for your organization.');
  } else if (status === 'error' && failure) {
    detail = webPushFailureText(failure, t);
  } else {
    detail = t('messaging.webPush.bellOff', 'Desktop notifications are off for this browser.');
  }

  const canEnable =
    pushOnAllDevices !== false && (status === 'default' || (status === 'error' && failure?.kind !== 'unsupported'));

  return (
    <div
      className="flex items-center gap-2 border-b border-gray-200 bg-amber-50 px-4 py-2 text-xs text-amber-900 dark:border-gray-700 dark:bg-amber-950/40 dark:text-amber-100"
      data-testid="bell-push-status"
    >
      <BellOff className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1">{detail}</p>
      {canEnable && (
        <button
          type="button"
          disabled={busy}
          // Synchronous on purpose: enable() raises the permission prompt before any await.
          onClick={() => {
            void enable().then((s) => {
              if (s === 'error' || s === 'denied') toastWebPushFailure(getLastWebPushFailure(), t);
            });
          }}
          className="flex-shrink-0 rounded-md bg-v2accent px-2.5 py-1 font-semibold text-white hover:bg-v2accent/90 disabled:opacity-60"
          data-testid="bell-push-enable"
        >
          {t('messaging.webPush.turnOn', 'Turn on')}
        </button>
      )}
    </div>
  );
}

export default BellPushStatus;
